import { type BuildFacetGraphParams, type ExplodeFlow, NodeRole } from "../../types";
import { buildFieldFocus } from "./buildFieldFocus";
import { buildFieldGrid } from "./buildFieldGrid";
import { titleEdge } from "./graphEdges";
import {
    COLUMN_GAP,
    GHOST_COUNT,
    GHOST_ZONE_SLOTS,
    NODE_WIDTH,
    OVERLAY_Z,
    ROOT_Y,
    TITLE_SLOT_HEIGHT,
    ZONE_LEFT_OFFSET,
    ZONE_ROW_GAP,
    ZONE_TOP_OFFSET,
    ZONE_WIDTH,
} from "./graphLayout";
import { graphNode } from "./graphNode";
import { rowTops } from "./rowTops";
import { zoneTitleNode } from "./zoneTitleNode";

const CENTER_ID = "value-focus";

export const buildBaseGraph = ({
    categories,
    categoryField,
    categoryLabel,
    rootLabel,
    gridPage = 0,
    selected,
    clusters,
    subLoading = false,
    fieldOptions,
}: BuildFacetGraphParams): ExplodeFlow => {
    if (fieldOptions && !selected) {
        return buildFieldGrid({ fieldOptions, rootLabel, gridPage });
    }

    if (!selected) {
        return buildFieldFocus({ categories, categoryField, categoryLabel, rootLabel });
    }

    const stepX = NODE_WIDTH + COLUMN_GAP;
    const rootNode = graphNode("root", 0, ROOT_Y, {
        label: rootLabel,
        role: NodeRole.ROOT,
        selection: [],
    });
    const valueFocusNode = graphNode(
        CENTER_ID,
        stepX,
        ROOT_Y,
        {
            label: selected.value,
            caption: categoryLabel,
            count: categories.find((bucket) => bucket.value === selected.value)?.count,
            role: NodeRole.GROUP,
            focused: true,
            selection: [selected],
        },
        OVERLAY_Z,
    );
    const centerX = stepX;
    const centerY = ROOT_Y;
    const rootEdge = titleEdge("root", CENTER_ID, "e-root-value");

    if (subLoading && clusters.length === 0) {
        const ghostRowTops: [number, number] = [
            centerY + ZONE_TOP_OFFSET,
            centerY + ZONE_TOP_OFFSET + TITLE_SLOT_HEIGHT + ZONE_ROW_GAP,
        ];
        const ghostNodes = Array.from({ length: GHOST_COUNT }, (_, index) =>
            graphNode(
                `g-${index}`,
                centerX + ZONE_LEFT_OFFSET + Math.floor(index / GHOST_ZONE_SLOTS) * ZONE_WIDTH,
                ghostRowTops[index % GHOST_ZONE_SLOTS],
                {
                    label: "",
                    role: NodeRole.GROUP,
                    selection: [],
                    skeleton: true,
                },
                OVERLAY_Z,
            ),
        );
        const ghostEdges = ghostNodes.map((ghost, index) =>
            titleEdge(CENTER_ID, ghost.id, `e-ghost-${index}`),
        );
        return {
            nodes: [rootNode, valueFocusNode, ...ghostNodes],
            edges: [rootEdge, ...ghostEdges],
        };
    }

    const tops = rowTops(centerY);
    const titleNodes = clusters.map((cluster, index) =>
        zoneTitleNode({ cluster, index, centerX, tops }),
    );
    const zoneEdges = titleNodes.map((titleNode, index) =>
        titleEdge(CENTER_ID, titleNode.id, `e-ttl-${index}`),
    );

    return {
        nodes: [rootNode, valueFocusNode, ...titleNodes],
        edges: [rootEdge, ...zoneEdges],
    };
};
